import { Separator } from "@radix-ui/react-separator";
import * as Tabs from "@radix-ui/react-tabs";
import clsx from "clsx";
import React, { useState } from "react";
import { FormattedMessage } from "react-intl";
import Skeleton from "react-loading-skeleton";
import { Link, useHistory, useParams } from "react-router-dom";

import { buildLocale } from "~/common/locale";
import { isQueryNotFoundError } from "~/common/query";
import { RedesignPageHeader } from "~/common/redesign-v1";
import { PersonName } from "~/components/common/PersonName";
import { RemoteLogErrorBoundary } from "~/components/common/remote-log-error-boundary";
import { useIntlDocumentTitle } from "~/hooks/use-document-title";
import { useLoadingDelay } from "~/hooks/use-loading-delay";
import type { DateString, Loading, NumericString } from "~/types/common";

import { LeftArrowIcon } from "./left-arrow-icon";
import { MeetingCard } from "./meeting-card";
import {
  useCancelMeetingMutation,
  useGetMeetingQuery,
  useRescheduleMeetingMutation,
} from "./meetings-api";
import { AnimateOpacity, MeetingsEmptyFallback, MeetingsErrorFallback } from "./meetings-shared";
import type { TMeeting } from "./meetings-types";
import { useIsRedesignMeetings } from "./redesign-meetings-context";

export function MeetingDetailPage() {
  useIntlDocumentTitle("meetings.detail.title");
  const { meeting_id } = useParams<{ meeting_id: NumericString }>();
  const meetingQuery = useGetMeetingQuery({ meetingId: meeting_id });

  return (
    <>
      <MeetingDetailHeader />

      <RemoteLogErrorBoundary
        component="meeting_detail_page"
        fallbackRender={MeetingsErrorFallback}
        onReset={meetingQuery.refetch}
      >
        <MeetingDetailContent />
      </RemoteLogErrorBoundary>
    </>
  );
}

function MeetingDetailHeader() {
  const history = useHistory();
  const redesign = useIsRedesignMeetings();

  // Redesign detail screens use the shell breadcrumb on desktop and an inline back button on mobile.
  if (redesign) {
    return <RedesignPageHeader className="tw-mb-6" backTo="/pages/meetings" title={<FormattedMessage id="meetings.detail" />} titleTestId="rd-meeting-detail-title" />;
  }

  const backButtonElement = (
    <button
      className={clsx(
        "tw-inline-flex tw-h-10 tw-w-10 tw-items-center tw-justify-center tw-rounded-xl",
        "focus-visible:tw-ring-offset-3 focus-visible:tw-ring-2 focus-visible:tw-ring-blue-600",
        "tw-border-none tw-bg-white tw-transition-colors hover:tw-bg-[#f4f4f5]",
      )}
      onClick={history.goBack}
    >
      <LeftArrowIcon className="tw-fill-brand-black" />

      <span className="tw-sr-only">
        <FormattedMessage id="reminder.back" />
      </span>
    </button>
  );

  return (
    <header className="tw-mb-7 tw-mt-3 tw-flex tw-items-center max-sm:tw-justify-between sm:tw-gap-7">
      {backButtonElement}

      <h1 className="tw-my-0 tw-text-center tw-text-xl tw-font-semibold tw-text-brand-black sm:tw-text-2xl">
        <FormattedMessage id="meetings.detail" />
      </h1>

      {/* To center h1 across the page with flexbox. */}
      <div className="tw-invisible sm:tw-hidden">{backButtonElement}</div>
    </header>
  );
}

function MeetingDetailContent() {
  const { meeting_id } = useParams<{ meeting_id: NumericString }>();
  const meetingQuery = useGetMeetingQuery({ meetingId: meeting_id });
  const isLoadingMinDuration = useLoadingDelay(meetingQuery.isLoading);

  if (meetingQuery.isUninitialized || meetingQuery.isLoading || isLoadingMinDuration) {
    return (
      <AnimateOpacity>
        <MeetingDetailCard meeting={undefined} />
      </AnimateOpacity>
    );
  }

  if (isQueryNotFoundError(meetingQuery.error)) {
    return (
      <MeetingsEmptyFallback
        title="meetings.detail.not_found"
        description="meetings.detail.not_found.description"
        link={{ to: "/pages/meetings/enrolled", children: "meetings.enrolled.title.all" }}
      />
    );
  }

  if (meetingQuery.isError) {
    return (
      <MeetingsErrorFallback
        title="meetings.detail.error"
        resetErrorBoundary={meetingQuery.refetch}
      />
    );
  }

  return <MeetingDetailCard meeting={meetingQuery.data} />;
}

function MeetingDetailCard({ meeting }: { meeting: Loading<TMeeting> }) {
  const redesign = useIsRedesignMeetings();
  const history = useHistory();
  const [selectedPatientIndex, setSelectedPatientIndex] = useState("0");
  const [cancelMeeting] = useCancelMeetingMutation();
  const [rescheduleMeeting] = useRescheduleMeetingMutation();

  return (
    <article
      className={clsx(
        "tw-flex max-desktop-lg:tw-flex-col max-desktop-lg:tw-gap-8",
        // The redesign shell supplies the card surface; the standalone legacy route supplies its own.
        redesign ? "tw-mb-0 tw-p-0" : "tw-mb-8 tw-rounded-3xl tw-bg-white tw-p-5 tw-shadow-sm sm:tw-p-8",
      )}
    >
      <Tabs.Root
        value={selectedPatientIndex}
        onValueChange={setSelectedPatientIndex}
        className={clsx(
          "tw-flex tw-grow tw-flex-col tw-gap-8 tw-border-y-0 tw-border-l-0",
          redesign ? "tw-border-r-border" : "tw-border-r-[#E4E4E4]",
          "desktop-lg:tw-mr-6 desktop-lg:tw-border-r desktop-lg:tw-border-solid desktop-lg:tw-pr-6",
        )}
      >
        <Tabs.List className="tw-flex tw-max-h-20 tw-gap-1">
          {meeting?.patients.map(({ patient }, index, patients) => {
            const isSelected = `${index}` == selectedPatientIndex && patients.length > 1;

            return (
              <Tabs.Trigger
                key={patient.patient_id}
                value={`${index}`}
                className={clsx(
                  "tw-flex tw-w-full tw-flex-col tw-gap-1 tw-rounded-xl tw-border tw-border-solid",
                  "tw-overflow-hidden tw-whitespace-nowrap tw-bg-transparent tw-p-3 tw-text-left sm:tw-p-4",
                  "tw-transition-colors",
                  redesign
                    ? ["tw-font-sans tw-text-sm tw-text-foreground hover:tw-bg-accent active:tw-bg-accent", isSelected ? "tw-border-muted-foreground" : "tw-border-border"]
                    : ["tw-text-brand-black hover:tw-bg-[#f4f4f5] active:tw-bg-[#f4f4f5]", isSelected ? "tw-border-brand-gray" : "tw-border-[#E9E9E9]"],
                )}
              >
                <Link
                  to={`/pages/patient/${patient.patient_id}`}
                  // A blue Bootstrap anchor is the one thing that reads as "not this design" inside an
                  // otherwise neutral card; the redesign has no link-blue, links are `foreground` + underline
                  // on hover (`ui/button.tsx`'s `link` variant).
                  className={clsx("tw-self-start tw-font-semibold", redesign && "tw-text-foreground tw-no-underline hover:tw-underline")}
                >
                  {patient.patient_id}
                </Link>

                <PersonName
                  person={patient}
                  className="tw-inline-block tw-max-w-full tw-overflow-hidden tw-overflow-ellipsis"
                />
              </Tabs.Trigger>
            );
          }) ?? <Skeleton containerClassName="tw-w-full" className="tw-rounded-xl" height={75} />}
        </Tabs.List>

        <div>
          {(meeting?.patients ?? [undefined]).map((meetingPatient, index) => {
            return (
              <Tabs.Content
                key={index}
                value={`${index}`}
                className="tw-flex tw-flex-col tw-gap-8"
                tabIndex={`${index}` == selectedPatientIndex ? 0 : -1}
              >
                <h2 className={clsx("tw-my-0 tw-text-base tw-font-semibold sm:tw-text-xl", redesign ? "tw-font-sans tw-text-foreground" : "tw-text-brand-black")}>
                  {meetingPatient?.theme ?? <Skeleton className="tw-max-w-[75%] tw-rounded-md" />}
                </h2>

                <div className={clsx("tw-flex tw-flex-col", redesign ? RD_FIELD_GROUP : "tw-gap-1")}>
                  <div className={redesign ? RD_FIELD_LABEL : "tw-text-brand-gray"}>
                    {meetingPatient ? (
                      <FormattedMessage id="meetings.your_question" />
                    ) : (
                      <Skeleton className="tw-rounded-md" width={100} />
                    )}
                  </div>

                  <p className={clsx("tw-m-0 tw-whitespace-pre-wrap", redesign ? RD_FIELD_VALUE : "tw-text-brand-black")}>
                    {meetingPatient?.question ?? <Skeleton className="tw-rounded-md" count={3} />}
                  </p>
                </div>

                <div className={clsx("tw-flex tw-flex-col", redesign ? RD_FIELD_GROUP : "tw-gap-1")}>
                  <div className={redesign ? RD_FIELD_LABEL : "tw-text-brand-gray"}>
                    {meetingPatient ? (
                      <FormattedMessage id="meetings.expert_comment" />
                    ) : (
                      <Skeleton className="tw-rounded-md" width={175} />
                    )}
                  </div>

                  <p
                    className={clsx(
                      "tw-m-0 tw-whitespace-pre-wrap",
                      redesign ? RD_FIELD_VALUE : "",
                      meetingPatient?.expert_comment ? (redesign ? "" : "tw-text-brand-black") : redesign ? "tw-text-muted-foreground" : "tw-text-brand-gray",
                    )}
                  >
                    {meetingPatient ? (
                      meetingPatient.expert_comment ?? (
                        <span className="tw-opacity-60">
                          <FormattedMessage id="meetings.expert_comment_default" />
                        </span>
                      )
                    ) : (
                      <Skeleton className="tw-rounded-md" count={2} />
                    )}
                  </p>
                </div>
              </Tabs.Content>
            );
          })}
        </div>
      </Tabs.Root>

      <Separator className={clsx("tw-h-px", redesign ? "tw-bg-border" : "tw-bg-[#D9DCE7]")} />

      <div className="tw-flex tw-flex-col tw-gap-8 sm:tw-min-w-[300px]">
        <div className={clsx("tw-flex tw-flex-col", redesign ? RD_FIELD_GROUP : "tw-gap-1")}>
          <div className={redesign ? RD_FIELD_LABEL : "tw-text-brand-gray"}>
            {meeting ? (
              <FormattedMessage id="meetings.datetime" />
            ) : (
              <Skeleton className="tw-max-w-[50%]" />
            )}
          </div>

          <p className={clsx("tw-m-0", redesign ? RD_FIELD_VALUE : "tw-font-semibold tw-text-brand-black")}>
            {meeting ? (
              formatStartDate(meeting.start_time_utc)
            ) : (
              <Skeleton className="tw-max-w-[75%]" />
            )}
          </p>
        </div>

        <MeetingCard
          padding={false}
          separator={false}
          shadow={false}
          dimFinished={false}
          displayDateBadges={false}
          displayDetailLink={false}
          key={meeting?.id}
          meeting={meeting}
          onCancel={(cancelReason?: string | null) => {
            return cancelMeeting({
              meetingId: meeting?.id.toString(),
              cancel_reason: cancelReason,
            })
              .unwrap()
              .then(() => history.push("/pages/meetings/enrolled"));
          }}
          onReschedule={(rescheduleTime: DateString, rescheduleReason?: string) => {
            rescheduleMeeting({
              meetingId: meeting?.id.toString(),
              reschedule_reason: rescheduleReason,
              start_time: rescheduleTime,
            }).unwrap();
          }}
        />
      </div>
    </article>
  );
}

/** The reference screen's label→value pair (`RedesignInfoRow`): label paragraph-small/SEMIBOLD, value
 *  paragraph-small/regular, 5px apart. Applied as CLASSES rather than by swapping in the component, because
 *  every one of these slots renders a `<Skeleton>` while the meeting loads and that branching lives inline. */
const RD_FIELD_GROUP = "tw-gap-[5px]";
const RD_FIELD_LABEL = "tw-font-sans tw-text-sm tw-font-semibold tw-leading-5 tw-text-foreground";
const RD_FIELD_VALUE = "tw-font-sans tw-text-sm tw-font-normal tw-leading-5 tw-text-foreground";

function formatStartDate(startTime: string) {
  const startDate = new Date(startTime);
  const dateStr = startDate.toLocaleDateString(buildLocale, { month: "long", day: "numeric" });
  const weekdayStr = startDate.toLocaleDateString(buildLocale, { weekday: "long" });
  const timeStr = startDate.toLocaleTimeString(buildLocale, { hour: "2-digit", minute: "2-digit" });
  return buildLocale == "ru-RU"
    ? `${dateStr}, ${weekdayStr}, в ${timeStr}`
    : `${dateStr}, ${weekdayStr}, ${timeStr}`;
}
